import React, { useState, useEffect } from 'react';
import { Button, Input, Textarea, Image, Checkbox } from '@nextui-org/react';
import { Tasks } from 'common/src/utils/types';
import { formatPrice, formatDate } from 'common/src/utils/functions';
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import { Comment } from './types';
import { supabase } from 'common/src/supabase';
import { toast } from 'react-toastify';
import { format, toZonedTime } from 'date-fns-tz';
import { es } from 'date-fns/locale';
import { updateTask } from 'common/src/api/tasks';

interface LeftColumnProps {
  task: Tasks & {
    description_image_url?: string;
    payment_method?: string;
  };
  askerAddress: string;
  askerCommune: string;
  askerCity?: string;
  taskDate: string;
  comments: Comment[];
  setIsDatePickerOpen: (isOpen: boolean) => void;
  handleAddComment: () => void;
  onTaskUpdate: (updatedTask: Partial<Tasks>) => void;
  category: { id: number; name: string } | null;
  taskers: { id: number; email: string }[];
}

const LeftColumn: React.FC<LeftColumnProps> = ({
  task,
  askerAddress,
  askerCommune,
  askerCity,
  taskDate,
  comments,
  setIsDatePickerOpen,
  handleAddComment,
  onTaskUpdate,
  category,
  taskers,
}) => {
  const [editedTask, setEditedTask] = useState<Partial<Tasks>>({});
  const [isTaskChanged, setIsTaskChanged] = useState(false);

  useEffect(() => {
    setEditedTask({});
    setIsTaskChanged(false);
  }, [task]);

  const handleInputChange = (
    field: keyof Tasks,
    value: string | number | boolean
  ) => {
    setEditedTask((prev) => {
      const newTask = { ...prev, [field]: value };
      if (value === task[field]) {
        delete newTask[field];
      }
      setIsTaskChanged(Object.keys(newTask).length > 0);
      return newTask;
    });
  };

  const handleSaveTask = async () => {
    try {
      if (Object.keys(editedTask).length === 0) {
        return;
      }
      const updatedTask = await updateTask(task.id, editedTask);
      if (updatedTask) {
        onTaskUpdate(editedTask);
        toast.success('Tarea actualizada correctamente');
        setEditedTask({});
        setIsTaskChanged(false);
      }
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Error al actualizar la tarea');
    }
  };

  const sendCommentToTaskers = async (comment: Comment) => {
    try {
      const { data, error } = await supabase.functions.invoke('sendEmail', {
        body: {
          emails: taskers.map((tasker) => tasker.email),
          subject: 'Nuevo comentario en para tu Task',
          content: `
            <h2>Nuevo comentario para Task</h2>
            <p>Nuevo comentario para "${task.description}"</p>
            <p><strong>Comentario:</strong> ${comment.comment}</p>
          `,
        },
      });

      if (error) throw error;
      console.log('Email sent successfully:', data);
      toast.success('Comentario enviado a los taskers exitosamente');
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Error al enviar el comentario a los taskers');
    }
  };

  const formatDateHuman = (date: string | null) => {
    if (!date) return 'No establecida';
    const chileanDate = toZonedTime(new Date(date), 'America/Santiago');
    return format(chileanDate, "EEEE d 'de' MMMM 'de' yyyy 'a las' HH:mm", {
      locale: es,
      timeZone: 'America/Santiago',
    });
  };

  return (
    <div className="p-3 border rounded-xl">
      <div className="mb-6">
        <h3 className="text-md font-semibold mb-2">Detalles del Task pedido</h3>
        <p className="text-sm">
          <span className="font-medium">Asker:</span> {task?.name}{' '}
          {task?.last_name}
        </p>
        <p className="text-sm">
          <span className="font-medium">Precio:</span>{' '}
          {formatPrice(task?.price)}
        </p>
        <p className="text-sm">
          <span className="font-medium">Estado:</span> {task?.status}
        </p>
        <p className="text-sm">
          <span className="font-medium">Método de pago:</span>{' '}
          {task?.payment_method ||
            'No especificado, seguramente transferencia.'}
        </p>
        <p className="text-sm">
          <span className="font-medium">Dirección:</span> {askerAddress}
        </p>
        <p className="text-sm">
          <span className="font-medium">Comuna:</span> {askerCommune}
        </p>
        <p className="text-sm">
          <span className="font-medium">Ciudad:</span> {askerCity}
        </p>
        <p className="text-sm">
          <span className="font-medium">Categoría:</span>{' '}
          {category ? category.name : 'No especificada'}
        </p>
      </div>

      <div className="mb-6">
        <h3 className="text-md font-semibold mb-2">Descripción</h3>
        <Textarea
          value={
            editedTask.description !== undefined
              ? editedTask.description
              : task.description
          }
          onChange={(e) => handleInputChange('description', e.target.value)}
          className="w-full"
          placeholder="Descripción de la tarea"
        />
        {task.description_image_url && (
          <div className="mt-4">
            <Image
              src={task.description_image_url}
              alt="Imagen de la descripción"
              className="max-w-full h-auto rounded-lg"
            />
          </div>
        )}
      </div>

      <div className="mb-6">
        <h3 className="text-md font-semibold mb-2">Precio (con Fee + IVA)</h3>
        <Input
          type="number"
          value={
            editedTask.price !== undefined
              ? editedTask.price.toString()
              : task.price?.toString()
          }
          onChange={(e) =>
            handleInputChange('price', parseFloat(e.target.value))
          }
          className="w-full"
          placeholder="Precio de la tarea"
        />
      </div>

      <div className="mb-6">
        <Checkbox
          isSelected={editedTask.is_recurring ?? task.is_recurring}
          onValueChange={(isSelected) =>
            handleInputChange('is_recurring', isSelected)
          }
        >
          Task recurrente
        </Checkbox>
      </div>

      <Button
        color="primary"
        onPress={handleSaveTask}
        isDisabled={!isTaskChanged}
        className="mb-6"
      >
        Guardar Cambios
      </Button>

      <div className="my-6 border-t border-gray-200"></div>

      <div className="mb-6">
        <div className="flex items-center gap-3">
          <h3 className="text-md font-semibold">Fecha de la Task</h3>
          <Button
            size="sm"
            color="primary"
            variant="flat"
            onPress={() => setIsDatePickerOpen(true)}
          >
            {taskDate ? 'Cambiar Fecha' : 'Establecer Fecha'}
          </Button>
        </div>
        <p className="text-sm mr-2">{formatDateHuman(taskDate)}</p>
      </div>

      <div className="my-6 border-t border-gray-200"></div>

      <div>
        <div className="mb-4">
          <div className="flex gap-2">
            <h3 className="text-xl font-semibold">Comentarios</h3>

            <Button
              size="sm"
              color="primary"
              variant="flat"
              isIconOnly
              onClick={handleAddComment}
              aria-label="Add comment"
            >
              <FaPlus />
            </Button>
          </div>
          <p className="text-sm italic text-gray-500">
            Recuerda que para enviar un comentario a los taskers por correo
            debes seleccionar la categoría
          </p>
        </div>
        <div className="space-y-6">
          {comments &&
            comments.map((comment) => (
              <div
                key={comment.id}
                className="bg-gray-50 rounded-lg p-4 shadow-sm"
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-semibold">{comment.User.first_name}</p>
                    <p className="text-xs text-gray-500">
                      {formatDate(comment.created_at)}
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      isIconOnly
                      variant="light"
                      aria-label="Edit comment"
                    >
                      <FaEdit />
                    </Button>
                    <Button
                      size="sm"
                      isIconOnly
                      variant="light"
                      color="danger"
                      aria-label="Delete comment"
                    >
                      <FaTrash />
                    </Button>
                  </div>
                </div>
                <p className="text-sm">{comment.comment}</p>
                <div className="mt-2">
                  <Button
                    size="sm"
                    color="primary"
                    variant="flat"
                    onClick={() => sendCommentToTaskers(comment)}
                    disabled={!category || taskers.length === 0}
                  >
                    Enviar a Taskers por correo ({taskers.length})
                  </Button>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default LeftColumn;
